import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs/dist/pptxgen.es.js";
import { useState } from "react";
import { useSelector } from "react-redux";

export const CHART_SELECTOR = "export-chart";
export const EXPORT_IGNORE_ATTRIBUTE = "data-export-ignore";
const DEFAULT_FILENAME = "Dashboard.pptx";

const PPTX_THEME = {
    slide: {
        width: 10,
        height: 5.625,
    },
    colors: {
        primary: "015294",
        secondary: "525252",
        footer: "666666",
    },
    fonts: {
        heading: 20,
        body: 14,
        footer: 10,
    },
    spacing: {
        margin: 0.5,
        titleY: 0.5,
        contentY: 1.5,
        contentHeight: 2.5,
        footerY: 5.3,
        footerHeight: 0.2,
        chartFooterGap: 0.6,
        chartVerticalOffset: 0.2,
    },
    chart: {
        scale: 2,
    },
    capture: {
        useCORS: true,
        allowTaint: true,
        logging: false,
        ignoreElements: (element) => {
            return element.hasAttribute(EXPORT_IGNORE_ATTRIBUTE);
        },
    },
};

const useExportGraphsToPptx = (filename = DEFAULT_FILENAME) => {
    const [isError, setIsError] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const { user } = useSelector((state) => state.auth);

    const getFilterText = (filters) => {
        const {
            selectedCoverageType,
            relatedOrganizationNames,
            selectedReportingPeriod,
            selectedCompanyYourCompanyTo,
            selectedAnalysisType,
            selectedPolicyDivision,
            selectedReportingGroup,
        } = filters;

        const appliedFilters = [
            {
                text: `Organization: ${filters.organizationName}\n`,
                options: { breakLine: true },
            },
        ];

        if (relatedOrganizationNames) {
            appliedFilters.push({
                text: `Related Organizations: ${relatedOrganizationNames}\n`,
                options: { breakLine: true },
            });
        }

        if (
            selectedCoverageType &&
            Array.isArray(selectedCoverageType) &&
            selectedCoverageType.length > 0
        ) {
            appliedFilters.push({
                text: `Coverage Type: ${selectedCoverageType[0].name}\n`,
                options: { breakLine: true },
            });
        }

        if (
            selectedAnalysisType &&
            Array.isArray(selectedAnalysisType) &&
            selectedAnalysisType.length > 0
        ) {
            appliedFilters.push({
                text: `Analysis Type: ${selectedAnalysisType[0].name}\n`,
                options: { breakLine: true },
            });
        }

        if (selectedReportingPeriod) {
            appliedFilters.push({
                text: `Reporting Period: ${selectedReportingPeriod.displayName}\n`,
                options: { breakLine: true },
            });
        }

        if (
            selectedCompanyYourCompanyTo &&
            Array.isArray(selectedCompanyYourCompanyTo) &&
            selectedCompanyYourCompanyTo.length > 0
        ) {
            const selectedCompanyYourCompanyToText =
                selectedCompanyYourCompanyTo.map((s) => s.name).join(", ");

            appliedFilters.push({
                text: `Compare Your Company To: ${selectedCompanyYourCompanyToText}\n`,
                options: { breakLine: true },
            });
        }

        if (
            selectedReportingGroup &&
            Array.isArray(selectedReportingGroup) &&
            selectedReportingGroup.length > 0
        ) {
            const selectedReportingGroupText = selectedReportingGroup
                .map((s) => s.name)
                .join(", ");

            appliedFilters.push({
                text: `Reporting Group: ${selectedReportingGroupText}\n`,
                options: { breakLine: true },
            });
        }

        if (
            selectedPolicyDivision &&
            Array.isArray(selectedPolicyDivision) &&
            selectedPolicyDivision.length > 0
        ) {
            const selectedPolicyDivisionText =
                selectedPolicyDivision.join(", ");

            appliedFilters.push({
                text: `Policies and Divisions: ${selectedPolicyDivisionText}`,
                options: { breakLine: true },
            });
        }

        return appliedFilters;
    };

    const getRanByUserName = () => {
        if (user.userLastName.trim() !== "") {
            return `${user.userLastName}, ${user.userFirstName}`;
        }

        return user.userFirstName;
    };

    const getFooterText = (currentDate) => {
        return `Run as of: ${currentDate}`;
    };

    const addFilterSlide = (pptx, filters, footer, ranByUserName) => {
        const slide = pptx.addSlide();

        const { width } = PPTX_THEME.slide;
        const { margin, titleY, contentY, contentHeight } = PPTX_THEME.spacing;

        slide.addText("Filters Applied", {
            x: margin,
            y: titleY,
            w: width - margin * 2,
            h: 0.4,
            fontSize: PPTX_THEME.fonts.heading,
            bold: true,
            color: PPTX_THEME.colors.primary,
        });

        slide.addText(filters, {
            x: margin,
            y: contentY,
            w: width - margin * 2,
            h: contentHeight,
            fontSize: PPTX_THEME.fonts.body,
            color: PPTX_THEME.fonts.secondary,
            wrap: true,
            valign: "mid",
        });

        addFooter(slide, footer, ranByUserName);
    };

    const addFooter = (slide, footer, ranBy) => {
        const { margin, footerY, footerHeight } = PPTX_THEME.spacing;

        const footerOptions = {
            fontSize: PPTX_THEME.fonts.footer,
            color: PPTX_THEME.colors.footer,
            y: footerY,
            h: footerHeight,
            margin: 0,
            valign: "mid",
        };

        slide.addText(footer, {
            x: margin,
            ...footerOptions,
            align: "left",
        });

        if (ranBy) {
            slide.addText(`Ran By: ${ranBy}`, {
                ...footerOptions,
                align: "right",
            });
        }
    };

    const captureChart = async (chart) => {
        return html2canvas(chart, {
            ...PPTX_THEME.capture,
            scale: PPTX_THEME.chart.scale,
        });
    };

    const getChartDimensions = (canvas) => {
        const { width, height } = PPTX_THEME.slide;
        const { margin, chartFooterGap, chartVerticalOffset } =
            PPTX_THEME.spacing;

        const maxWidth = width - margin * 2;
        const maxHeight = height - margin * 2 - chartFooterGap;

        const aspectRatio = canvas.width / canvas.height;

        let w = maxWidth;
        let h = w / aspectRatio;

        if (h > maxHeight) {
            h = maxHeight;
            w = h * aspectRatio;
        }

        const xPosition = (width - w) / 2;
        const yPosition = (height - h) / 2 - chartVerticalOffset;

        return {
            x: xPosition,
            y: yPosition,
            w,
            h,
        };
    };

    const addChartSlide = (pptx, canvas, footer) => {
        const slide = pptx.addSlide();

        const dimensions = getChartDimensions(canvas);

        slide.addImage({
            data: canvas.toDataURL("image/png"),
            ...dimensions,
        });

        addFooter(slide, footer);
    };

    const exportGraphs = async (data) => {
        if (isExporting) return;

        setIsExporting(true);
        setIsError(true);

        try {
            const charts = Array.from(
                document.querySelectorAll(`.${CHART_SELECTOR}`)
            );

            if (charts.length === 0) {
                throw new Error("No charts found.");
            }

            const pptx = new PptxGenJS();
            const ranByUserName = getRanByUserName();
            pptx.author = ranByUserName;

            const filters = getFilterText(data.filters);
            const footer = getFooterText(data.currentDate);

            addFilterSlide(pptx, filters, footer, ranByUserName);

            for (const chart of charts) {
                const canvas = await captureChart(chart);

                addChartSlide(pptx, canvas, footer);
            }

            await pptx.writeFile(filename);
        } catch (e) {
            console.error("Export Failed: ", e);
            setIsError(true);
        } finally {
            setIsExporting(false);
        }
    };

    return { exportGraphs, isExporting, isError };
};

export default useExportGraphsToPptx;
